import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import NormalInput from '../../components/UI/Input/NormalInput/NormalInput';
import Container from 'react-bootstrap/Container';
import { toast, ToastContainer } from 'react-toastify';
import { useEffect, useState } from 'react';
import Loading from '../../components/Loading/Loading';
import LottieConfig from '../../components/UI/LottieConfig/LottieConfig';
import errorMessage from '../../assets/lotties/error.json';
import schemas from '../../utils/Schemas';
import { RootState } from '../../store/config/types';
import { executeRegisterSubscriber } from '../../store/action_creators/subscriber.actions';
import { AppActions } from '../../store/config/ActionTypes';
import { ThunkDispatch } from 'redux-thunk';
import { connect, ConnectedProps } from 'react-redux';
import { SubscriberRequest } from '../../types/subscriber/request';
import { getSubscriptionType } from '../../store/action_creators/subscriptionTypes.actions';
import { amountHelper } from '../../utils/amountHelper';
import useQuery from '../../hooks/useQuery';
import Error404 from '../Error404/Error404';
import { Frequence } from '../../types/enums/frequence.enum';

interface Values {
  firstName: string;
  lastName: string;
  email: string;
}

interface LandingProps {
  setFullName: React.Dispatch<React.SetStateAction<string>>;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
}

const mapStateToProps = (state: RootState) => ({
  subscriber: state.subscriber,
  subscriptionTypes: state.subscriptionTypes,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, any, AppActions>) => ({
  executeRegisterSubscriber: (subscriberRequest: SubscriberRequest) =>
    dispatch(executeRegisterSubscriber(subscriberRequest)),
  getSubscriptionType: (subscriptionTypeId: number) => dispatch(getSubscriptionType(subscriptionTypeId)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type PropsType = LandingProps & PropsFromRedux;

function Landing({
  setFullName,
  setEmail,
  executeRegisterSubscriber,
  getSubscriptionType,
  subscriptionTypes,
  subscriber,
}: PropsType) {
  const navigate = useNavigate();
  const queryParams = useQuery();
  const [subscriptionTypeId, setSubscriptionTypeId] = useState(queryParams.get('subscriptionTypeId'));
  const canChooseSubscription = process.env.REACT_APP_CAN_CHOOSE_SUBSCRIPTION === 'true';

  useEffect(() => {
    if (
      !subscriptionTypes.loadingSubscriptionTypes &&
      subscriptionTypes.subscriptionTypes === null &&
      subscriptionTypes.subscriptionTypesErrorMessage === null
    ) {
      getSubscriptionType(Number(subscriptionTypeId));
    }
  }, [subscriptionTypes, getSubscriptionType, subscriptionTypeId]);

  const submit = async (values: Values) => {
    const subscriberRequest = {
      email: values.email,
      name: values.firstName,
      postalCode: '',
      fullName: values.firstName + ' ' + values.lastName,
      subscriptionTypeId: String(subscriptionTypeId),
    };

    if (!subscriber.registeringSubscriber) {
      executeRegisterSubscriber(subscriberRequest);
      setFullName(subscriberRequest.fullName);
      setEmail(subscriberRequest.email);

      navigate('/plexo');
    } else {
      toast.error(`Ocurrió un error al enviar la información - Intentelo más tarde`);
    }
  };

  if (!subscriptionTypeId && canChooseSubscription) {
    return <Error404 />;
  }

  return (
    <>
      <Container>
        <Formik
          onSubmit={submit}
          initialValues={{ firstName: '', lastName: '', email: '' }}
          validationSchema={schemas.UserInfoSchema}
        >
          {({ isValid }) => (
            <Form>
              <div className="landing">
                <div className="left-card">
                  <div className="title">Datos personales</div>
                  <Field name="firstName" component={NormalInput} label="Nombre" />
                  <Field name="lastName" component={NormalInput} label="Apellido" />
                  <Field name="email" component={NormalInput} label="Email" />
                </div>
                <div className="right-card">
                  {subscriptionTypes.loadingSubscriptionTypes ? (
                    <div className="loading">
                      <Loading />
                    </div>
                  ) : subscriptionTypes.subscriptionTypesErrorMessage !== null ? (
                    <div className="error">
                      <LottieConfig animationData={errorMessage} customHeight={100} customWidth={100} />
                      <span>
                        Ocurrió un error al obtener el tipo de suscripción. Prueba nuevamente más tarde.
                      </span>
                    </div>
                  ) : !subscriptionTypes.selectedSubscriptionType ? (
                    <div className="error">
                      <span>
                        No encontramos el tipo de suscripción especificado, revisa la URL e intenta nuevamente
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="right-card-title">
                        <p className="title">Donación mensual</p>
                      </div>
                      {!canChooseSubscription ? (
                        <div className="dues-cards">
                          {subscriptionTypes.subscriptionTypes?.map((subscription) => {
                            return (
                              <div className="dues-card" key={subscription.id}>
                                <div className="dues-title">{subscription.name}</div>
                                <div className="frequency-title">
                                  frecuencia {Frequence[subscription.frequence]}
                                </div>
                                <div className="dues">
                                  <div className="cuot">
                                    {subscription.recurrence === 0 ? '' : `${subscription.recurrence}x`}
                                  </div>
                                  <div className="price">
                                    {amountHelper.subscriptionCurrencyToken(subscription.currency)}{' '}
                                    {subscription.subscriptionBaseCost}
                                  </div>
                                </div>
                                {/* <div className="dues-description">10% off al contado</div> */}
                                <button
                                  className="dues-btn"
                                  type="submit"
                                  onClick={() => setSubscriptionTypeId(subscription.id.toString())}
                                  disabled={!isValid}
                                >
                                  <div className="dues-btn-description">Seleccionar</div>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="single-dues-card">
                          <div className="single-dues">
                            <div className="single-cuot">
                              {subscriptionTypes.selectedSubscriptionType.recurrence}x
                            </div>
                            <div className="single-price">
                              {amountHelper.subscriptionCurrencyToken(
                                subscriptionTypes.selectedSubscriptionType.currency,
                              )}{' '}
                              {subscriptionTypes.selectedSubscriptionType.subscriptionBaseCost}
                            </div>
                          </div>
                          <div className="single-dues-description">
                            Recordá que recibirás cargos mensuales independientes, por lo que el saldo de tu
                            tarjeta no se verá comprometido más allá del valor de una cuota
                          </div>
                          <button className="single-dues-btn" type="submit" disabled={!isValid}>
                            <div className="single-dues-btn-description">Continuar</div>
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Container>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

export default connector(Landing);
